// --- Add this helper component (same file or a small separate file) ---
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Target, Award } from 'lucide-react';

export function FlipBookCard({
  primary = '#6366f1',
  secondary = '#8b5cf6',
  mission,
  vision,
  cycleMs = 6000,            // time between flips
}) {
  const prefersReduced = usePrefersReducedMotion();
  const [flipped, setFlipped] = useState(false);
  const [paused, setPaused] = useState(false);
  const rootRef = useRef(null);

  // Auto-alternate Mission <-> Vision
  useEffect(() => {
    if (prefersReduced) return; // no animation
    if (paused) return;
    const id = setInterval(() => setFlipped((f) => !f), cycleMs);
    return () => clearInterval(id);
  }, [cycleMs, paused, prefersReduced]);

  // Pause when not visible (saves GPU + feels snappier)
  useEffect(() => {
    const el = rootRef.current;
    if (!el || prefersReduced) return;
    const io = new IntersectionObserver(([entry]) => setPaused(!entry.isIntersecting), { threshold: 0.25 });
    io.observe(el);
    return () => io.disconnect();
  }, [prefersReduced]);

  return (
    <div
      ref={rootRef}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      className="relative rounded-2xl shadow-lg border border-gray-200/60 bg-white/80 backdrop-blur-sm"
      style={{
        padding: '1.75rem',
        perspective: '1200px',
      }}
    >
      {/* slim spine */}
      <span
        className="absolute left-0 top-0 h-full w-[6px] rounded-l-2xl"
        style={{ background: `linear-gradient(180deg, ${primary}, ${secondary})` }}
        aria-hidden
      />

      {/* “Book” with two pages */}
      <div
        className="relative"
        style={{
          height: '100%',
          minHeight: '220px',
        }}
      >
        <div
          className="relative w-full h-full transition-transform duration-[900ms] ease-[cubic-bezier(.19,1,.22,1)]"
          style={{
            transformStyle: 'preserve-3d',
            transform: prefersReduced ? 'none' : `rotateY(${flipped ? 180 : 0}deg)`,
          }}
          aria-live="polite"
        >
          {/* Front Page — Mission */}
          <FlipPage
            side="front"
            tint={hexWithAlpha(primary, 0.10)}
            accent={primary}
            title="Our Mission"
            icon={<Target className="h-6 w-6" style={{ color: primary }} />}
            text={mission || 'We exist to inspire, enable, and uplift every learner through purposeful teaching and community impact.'}
          />

          {/* Back Page — Vision */}
          <FlipPage
            side="back"
            tint={hexWithAlpha(secondary, 0.10)}
            accent={secondary}
            title="Our Vision"
            icon={<Award className="h-6 w-6" style={{ color: secondary }} />}
            text={vision || 'A thriving ecosystem where innovation, equity, and excellence in education shape Africa’s future.'}
          />
        </div>
      </div>

      {/* tiny hint + controls */}
      <div className="mt-10 flex items-center justify-between">
        <div className="text-xs text-gray-500">
          {paused ? 'Paused' : 'Auto-flipping'} • Hover to pause
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            className="text-xs px-3 py-1 rounded-lg border border-gray-200 hover:bg-gray-50"
            onClick={() => setFlipped(false)}
            aria-label="Show Mission"
          >
            Mission
          </button>
          <button
            type="button"
            className="text-xs px-3 py-1 rounded-lg border border-gray-200 hover:bg-gray-50"
            onClick={() => setFlipped(true)}
            aria-label="Show Vision"
          >
            Vision
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------- internal pieces ---------- */

function FlipPage({ side = 'front', tint, accent, title, icon, text }) {
  return (
    <article
      className="absolute inset-0 rounded-xl border border-gray-200/60 shadow-sm"
      style={{
        background: '#ffffff',
        backfaceVisibility: 'hidden',
        transform: side === 'back' ? 'rotateY(180deg)' : 'rotateY(0deg)',
      }}
    >
      {/* subtle page tint */}
      <div
        className="absolute inset-0 rounded-xl"
        style={{ background: `linear-gradient(180deg, ${tint}, transparent 60%)` }}
        aria-hidden
      />
      <div className="relative p-2 sm:p-3 md:p-4 lg:p-5 xl:p-6">
        <div className="flex items-center gap-3 mb-3">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center shadow-sm"
            style={{ background: tint }}
          >
            {icon}
          </div>
          <h3 className="text-lg md:text-xl font-bold text-gray-900">{title}</h3>
        </div>
        <p className="text-gray-600 leading-relaxed">{text}</p>

        {/* page curl hint */}
        <span
          className="absolute bottom-0 right-0 w-10 h-10 rounded-tl-xl"
          style={{
            background:
              'radial-gradient(22px 22px at 100% 100%, rgba(0,0,0,.08) 0, rgba(0,0,0,0) 70%)',
          }}
          aria-hidden
        />
        {/* thin edge highlight */}
        <span
          className="absolute top-0 right-0 h-full w-[2px] opacity-60"
          style={{ background: `linear-gradient(180deg, ${hexWithAlpha(accent, 0.6)}, transparent)` }}
          aria-hidden
        />
      </div>
    </article>
  );
}

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReduced(!!mq.matches);
    update();
    mq.addEventListener?.('change', update);
    return () => mq.removeEventListener?.('change', update);
  }, []);
  return reduced;
}

function hexWithAlpha(hex, a = 0.12) {
  let h = hex.replace('#', '');
  if (h.length === 3) h = h.split('').map(c => c + c).join('');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${a})`;
}
